import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { usePkSummary, type PkTicket } from "../api/parkingApi";
import { ConsolePage, KpiRow, KpiTile, LoadState, Panel, StatusChip, ToolButton, relativeTime, toneOf, useTicker } from "../components/console/Console";
import { LOT_ICON, LotSelect, OccupancyBar, PARKING_BASE, PlateBadge, TICKET_TONE, chartGrid, chartTick, chartTooltip, duration, minutesBetween, money } from "./parkingUi";

// ===========================================================================
// Control de estacionamientos → Tablero: KPIs reales (ocupación, tickets
// abiertos, ingresos del día, estancia promedio), ocupación por estacionamiento,
// entradas/salidas por hora, ingresos de 14 días y últimos movimientos.
// ===========================================================================

export function TicketRow({ tk, now, onClick }: { tk: PkTicket; now: number; onClick?: () => void }) {
  const { t, i18n } = useTranslation("parking");
  const at = tk.exit_at || tk.entry_at;
  return (
    <li>
      <button type="button" onClick={onClick} className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-subtle">
        {tk.entry_photo_url ? (
          <img src={tk.entry_photo_url} alt="" loading="lazy" className="h-10 w-16 shrink-0 rounded object-cover" />
        ) : (
          <span className="flex h-10 w-16 shrink-0 items-center justify-center rounded bg-subtle text-content-muted"><i className="ri-car-line" /></span>
        )}
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5 text-sm font-medium text-content">
            <span className="font-mono">{tk.folio}</span>
            <PlateBadge plate={tk.plate} />
          </span>
          <span className="block truncate text-xs text-content-secondary">
            {tk.lot_name} · {tk.space_code ?? "—"} · {duration(minutesBetween(tk.entry_at, tk.exit_at, now))}
            {tk.amount?.total ? ` · ${money(tk.amount.total, i18n.language)}` : ""}
          </span>
        </span>
        <span className="shrink-0 text-right">
          <StatusChip tone={toneOf(TICKET_TONE, tk.status)}>{t(`ticketStatus.${tk.status}`)}</StatusChip>
          <span className="mt-0.5 block text-[11px] text-content-muted">{relativeTime(at, i18n.language, now)}</span>
        </span>
      </button>
    </li>
  );
}

export default function ParkingDashboard() {
  const { t, i18n } = useTranslation("parking");
  const navigate = useNavigate();
  const now = useTicker(15_000);
  const [lot, setLot] = useState("");
  const q = usePkSummary(lot || undefined, { refetchInterval: 20_000 });
  const s = q.data;

  const hourly = (s?.hourly ?? []).filter((h) => h.hour >= 5).map((h) => ({ name: `${String(h.hour).padStart(2, "0")}h`, [t("dashboard.entries")]: h.entries, [t("dashboard.exits")]: h.exits }));
  const daily = (s?.revenue.daily ?? []).map((d) => ({
    name: new Intl.DateTimeFormat(i18n.language, { day: "2-digit", month: "short" }).format(new Date(`${d.day}T12:00:00Z`)),
    [t("dashboard.ticketsRevenue")]: d.tickets,
    [t("dashboard.pensionsRevenue")]: d.pensions,
  }));
  const pensionsActive = (s?.pensions.active ?? 0) + (s?.pensions.expiring ?? 0);

  return (
    <ConsolePage
      icon="ri-parking-box-line"
      title={t("dashboard.title")}
      subtitle={t("dashboard.subtitle")}
      actions={
        <>
          <LotSelect lots={s?.lots ?? []} value={lot} onChange={setLot} />
          <ToolButton icon="ri-layout-grid-line" onClick={() => navigate(`${PARKING_BASE}/ocupacion`)}>{t("dashboard.openMap")}</ToolButton>
          <ToolButton icon="ri-cash-line" variant="primary" onClick={() => navigate(`${PARKING_BASE}/cobro`)}>{t("dashboard.charge")}</ToolButton>
        </>
      }
    >
      <LoadState loading={q.isLoading} error={q.error} empty={false} onRetry={() => q.refetch()}>
        {s && (
          <>
            {(s.refunds?.length ?? 0) > 0 && (
              <div role="alert" className="rounded-xl border border-error/40 bg-error/10 px-3 py-2 text-sm text-content">
                <p className="font-semibold"><i className="ri-refund-2-line text-error" /> {t("dashboard.refundsTitle", { interpolation: { escapeValue: false }, count: s.refunds!.length })}</p>
                <ul className="mt-1 space-y-0.5 text-xs text-content-secondary">
                  {s.refunds!.slice(0, 5).map((p) => (
                    <li key={p.id}>{p.folio ?? p.holder_name} · {money(p.amount, i18n.language)} · {t(`method.${p.method}`)} · {t(`refundReason.${p.refund_reason ?? "other"}`, { interpolation: { escapeValue: false }, defaultValue: p.refund_reason ?? "" })}</li>
                  ))}
                </ul>
              </div>
            )}
            <KpiRow>
              <KpiTile icon="ri-pie-chart-2-line" tone={s.totals.occupancy_pct >= 90 ? "error" : s.totals.occupancy_pct >= 70 ? "warning" : "success"} label={t("kpi.occupancy")} value={`${s.totals.occupancy_pct} %`} hint={t("kpi.freeOf", { interpolation: { escapeValue: false }, free: s.totals.free, total: s.totals.capacity })} />
              <KpiTile icon="ri-ticket-2-line" tone="info" label={t("kpi.openTickets")} value={s.totals.open_tickets} hint={s.totals.lost_tickets ? t("kpi.lostN", { interpolation: { escapeValue: false }, count: s.totals.lost_tickets }) : undefined} />
              <KpiTile icon="ri-money-dollar-circle-line" tone="success" label={t("kpi.revenueToday")} value={money(s.totals.revenue_today, i18n.language)} hint={t("kpi.paymentsN", { interpolation: { escapeValue: false }, count: s.totals.payments_today })} />
              <KpiTile icon="ri-timer-line" tone="primary" label={t("kpi.avgStay")} value={duration(s.totals.avg_stay_minutes)} hint={t("kpi.exitsN", { interpolation: { escapeValue: false }, count: s.totals.exits_today })} />
              <KpiTile icon="ri-login-box-line" tone="neutral" label={t("kpi.entriesToday")} value={s.totals.entries_today} />
              <KpiTile icon="ri-vip-crown-line" tone="warning" label={t("kpi.pensions")} value={pensionsActive} hint={t("kpi.expiringN", { interpolation: { escapeValue: false }, count: s.pensions.expiring ?? 0 })} />
            </KpiRow>

            <div className="grid gap-4 xl:grid-cols-5">
              <Panel className="xl:col-span-2" title={t("dashboard.byLot")} icon="ri-building-line" bodyClassName="p-2">
                <ul className="space-y-2">
                  {s.occupancy.map((o) => {
                    const l = s.lots.find((x) => x.id === o.lot_id);
                    return (
                      <li key={o.lot_id}>
                        <button type="button" onClick={() => navigate(`${PARKING_BASE}/ocupacion?lot=${encodeURIComponent(o.lot_id)}`)} className="flex w-full items-center gap-3 rounded-lg border border-line p-2 text-left hover:bg-subtle">
                          {l?.photo_url ? <img src={l.photo_url.replace("/estacionamientos/", "/estacionamientos/thumbs/")} alt="" className="h-14 w-24 shrink-0 rounded object-cover" loading="lazy" /> : <span className="flex h-14 w-24 items-center justify-center rounded bg-subtle"><i className={LOT_ICON[l?.type ?? "open"]} /></span>}
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-semibold text-content">{o.name}</span>
                            <span className="mb-1 block text-[11px] text-content-secondary">
                              <i className={LOT_ICON[l?.type ?? "open"]} /> {t(`lotType.${l?.type ?? "open"}`)} · {t("dashboard.freeN", { interpolation: { escapeValue: false }, count: o.by_status.free })} · {t("dashboard.openN", { interpolation: { escapeValue: false }, count: o.open_tickets })}
                            </span>
                            <OccupancyBar pct={o.occupancy_pct} />
                          </span>
                          <span className="w-12 shrink-0 text-right text-lg font-bold tabular-nums text-content">{o.occupancy_pct}%</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </Panel>
              <Panel className="xl:col-span-3" title={t("dashboard.recent")} icon="ri-pulse-line" bodyClassName="max-h-[380px] overflow-y-auto" actions={<span className="flex items-center gap-1.5 text-[11px] text-content-muted"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-success" />{t("common.live")}</span>}>
                <ul className="divide-y divide-line">
                  {s.recentTickets.map((tk) => (
                    <TicketRow key={tk.id} tk={tk} now={now} onClick={() => navigate(`${PARKING_BASE}/tickets?folio=${encodeURIComponent(tk.folio)}`)} />
                  ))}
                </ul>
              </Panel>
            </div>

            <div className="grid gap-4 xl:grid-cols-2">
              <Panel title={t("dashboard.perHour")} icon="ri-bar-chart-2-line">
                <div className="h-60">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={hourly}>
                      <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} vertical={false} />
                      <XAxis dataKey="name" tick={chartTick} axisLine={false} tickLine={false} interval={1} />
                      <YAxis allowDecimals={false} tick={chartTick} axisLine={false} tickLine={false} width={28} />
                      <Tooltip {...chartTooltip} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey={t("dashboard.entries")} fill="rgb(var(--c-info))" radius={[3, 3, 0, 0]} />
                      <Bar dataKey={t("dashboard.exits")} fill="rgb(var(--c-success))" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Panel>
              <Panel title={t("dashboard.revenue14")} icon="ri-line-chart-line">
                <div className="h-60">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={daily}>
                      <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} vertical={false} />
                      <XAxis dataKey="name" tick={chartTick} axisLine={false} tickLine={false} interval={1} />
                      <YAxis tick={chartTick} axisLine={false} tickLine={false} width={48} tickFormatter={(v: number) => `$${Math.round(v / 100) / 10}k`} />
                      <Tooltip {...chartTooltip} formatter={(v: number) => money(v, i18n.language)} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey={t("dashboard.ticketsRevenue")} stackId="r" fill="rgb(var(--c-primary))" />
                      <Bar dataKey={t("dashboard.pensionsRevenue")} stackId="r" fill="rgb(var(--c-warning))" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Panel>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <Panel title={t("dashboard.methodsToday")} icon="ri-wallet-3-line">
                <ul className="space-y-1.5 text-sm">
                  {(["cash", "card_terminal", "stripe", "link", "free"] as const).map((m) => (
                    <li key={m} className="flex items-center justify-between">
                      <span className="text-content-secondary">{t(`method.${m}`)}</span>
                      <span className="font-semibold tabular-nums text-content">{money(s.revenue.by_method_today[m], i18n.language)}</span>
                    </li>
                  ))}
                </ul>
              </Panel>
              <Panel title={t("dashboard.pensionsPanel")} icon="ri-vip-crown-line" actions={<button type="button" className="text-xs text-primary-500 hover:underline" onClick={() => navigate(`${PARKING_BASE}/pensiones`)}>{t("common.viewAll")}</button>}>
                <ul className="space-y-1.5 text-sm">
                  {(["active", "expiring", "expired", "suspended"] as const).map((k) => (
                    <li key={k} className="flex items-center justify-between">
                      <span className="text-content-secondary">{t(`pensionStatus.${k}`)}</span>
                      <span className="font-semibold tabular-nums text-content">{s.pensions[k] ?? 0}</span>
                    </li>
                  ))}
                </ul>
              </Panel>
              <Panel title={t("dashboard.invoicesPanel")} icon="ri-file-text-line" actions={<button type="button" className="text-xs text-primary-500 hover:underline" onClick={() => navigate(`${PARKING_BASE}/facturacion`)}>{t("common.viewAll")}</button>}>
                <ul className="space-y-1.5 text-sm">
                  {(["requested", "ready_to_stamp", "issued", "rejected"] as const).map((k) => (
                    <li key={k} className="flex items-center justify-between">
                      <span className="text-content-secondary">{t(`invoiceStatus.${k}`)}</span>
                      <span className="font-semibold tabular-nums text-content">{s.invoices[k] ?? 0}</span>
                    </li>
                  ))}
                </ul>
              </Panel>
            </div>
          </>
        )}
      </LoadState>
    </ConsolePage>
  );
}
